import React from "react";
import styled, { css } from "styled-components";
import Label from "./Label";
import { Check } from "react-feather";
import Description from "./Description";

interface Props {
  label?: React.ReactNode;
  value: boolean;
  onChange?: (value: boolean) => void;
  inlineLabel?: boolean;
  className?: string;
  disabled?: boolean;
  showBox?: boolean;
  showRadio?: boolean;
  noPadding?: boolean;
  stopPropagation?: boolean;
  padBox?: boolean;
  description?: string;
  leftLabel?: boolean;
}

const StyledLabel = styled(Label)`
  padding-bottom: 0;

  > span {
    padding-right: 0;
  }
`;

const StyledCheckbox = styled.button<{
  $selected?: boolean;
  $disabled?: boolean;
  $leftLabel?: boolean;
}>`
  display: flex;
  margin-top: 5px;
  margin-bottom: 5px;
  background: none;
  border: none;
  text-align: left;
  padding: 0;
  color: var(--gray400);
  font-size: 16px;
  align-items: center;
  transition: all 100ms;
  /* min-height: 25px; */
  font-family: inherit;
  text-transform: none;

  &:hover {
    // reset cantemo styles
    background: none;
    border: none;
    box-shadow: none;

    /* ${StyledLabel} {
      color: var(--gray500);
    } */
  }

  > label {
    margin-left: 8px;
    display: flex;
    align-items: flex-start;
    cursor: pointer;

    ${(props) =>
      props.$leftLabel
        ? css`
            margin-left: 0;
            margin-right: 8px;
          `
        : ""}

    ${(props) =>
      !props.$selected &&
      css`
        opacity: 0.6;
      `}
  }

  ${(props) =>
    props.$disabled &&
    css`
      opacity: 0.3;
      pointer-events: none;
    `}
`;

const Slider = styled.div<{ $selected?: boolean }>`
  width: 43px;
  height: 20px;
  border-radius: 10px;
  background-color: var(--dark060);
  border: 1px solid var(--dark400);
  transition: all 150ms;
  position: relative;
  margin-right: 2px;
  margin-left: 2px;

  ${(props) =>
    props.$selected &&
    css`
      background-color: var(--greenBackground);
      border-color: var(--greenBorderOverlay);
    `}
`;

const SliderButton = styled.div<{ $selected?: boolean }>`
  width: 25px;
  height: 25px;
  position: relative;
  top: -3.5px;
  left: -2px;
  border-radius: 50%;
  background: var(--dark400);
  transition: all 150ms;
  border: 1px solid var(--dark600);

  ${StyledCheckbox}:hover & {
    background: var(--dark600);
    border-color: var(--dark700);
  }

  ${(props) =>
    props.$selected &&
    css`
      transform: translate3d(20px, 0, 0);
      background: var(--green100);
      border-color: var(--green300);

      ${StyledCheckbox}:hover & {
        background: var(--green200);
        border-color: var(--green500);
      }
    `}
`;

const BoxPadding = styled.div<{ $pad?: boolean }>`
  ${(props) =>
    props.$pad &&
    css`
      padding: 6.5px;
      border-radius: 7px;

      ${StyledCheckbox}:hover & {
        background-color: var(--blue400a);
      }
    `}
`;

const Box = styled.div<{ $selected?: boolean; $radio?: boolean }>`
  width: 17px;
  height: 17px;
  background: var(--dark500);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  border: 1px solid var(--dark600);

  ${(props) =>
    props.$radio &&
    css`
      border-radius: 50%;
    `}

  ${StyledCheckbox}:hover & {
    background: var(--dark600);
    border-color: var(--dark700);
  }

  ${(props) =>
    props.$selected &&
    css`
      background: var(--blue060);
      color: var(--gray500);
      border-color: var(--blue100);

      ${StyledCheckbox}:hover & {
        background: var(--blue100);
        border-color: var(--blue200);
      }
    `}
`;

const Circle = styled.div<{ $selected?: boolean }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;

  ${(props) =>
    props.$selected &&
    css`
      background-color: var(--gray500);
    `}
`;

const Wrapper = styled.div<{ $pad: boolean; $extraPad: boolean }>`
  ${(props) =>
    props.$pad &&
    css`
      margin-bottom: 10px;
    `}

  ${(props) =>
    props.$extraPad &&
    css`
      margin-bottom: 20px;
    `}
`;

const Checkbox = (props: Props) => {
  const {
    label,
    onChange,
    value,
    inlineLabel,
    className,
    disabled,
    showBox,
    noPadding,
    stopPropagation,
    padBox,
    description,
    showRadio,
    leftLabel,
  } = props;

  return (
    <Wrapper
      $pad={!!label && !noPadding}
      $extraPad={!!description && !noPadding}
      className={className}
    >
      {label && !inlineLabel && <Label label={label} />}
      <StyledCheckbox
        onClick={(event) => {
          if (stopPropagation) {
            event.stopPropagation();
          }
          if (!disabled) {
            onChange?.(!value);
          }
        }}
        $selected={value}
        $disabled={disabled}
        $leftLabel={leftLabel}
      >
        {label && inlineLabel && leftLabel && <StyledLabel label={label} />}
        {showBox ? (
          <BoxPadding $pad={padBox}>
            <Box $selected={value}>
              <Check size={15} />
            </Box>
          </BoxPadding>
        ) : showRadio ? (
          <BoxPadding $pad={padBox}>
            <Box $selected={value} $radio>
              <Circle $selected={value} />
            </Box>
          </BoxPadding>
        ) : (
          <Slider $selected={value}>
            <SliderButton $selected={value} />
          </Slider>
        )}
        {label && inlineLabel && !leftLabel && <StyledLabel label={label} />}
      </StyledCheckbox>
      {description && <Description label={description} />}
    </Wrapper>
  );
};

export default Checkbox;
